/**
 * Lineage page - DEMO / DESIGN PREVIEW data only.
 *
 * Until 2026-05-14 the Lineage page rendered Cytoscape from this hand-
 * curated 17-node / 18-edge catalogue and surfaced its computed counts
 * as if they were live tenant metrics. That was caught in the Phase 2
 * hardening audit and fixed: the page now reads from
 * `getAccessGraph()` and only falls back to this catalogue if the URL
 * carries `?demo=1` (marketing screenshots, design QA, talking through
 * UX in a meeting where no tenant is connected).
 *
 * Do NOT consume this from a production code path. Importing
 * `?demo=1`-gated data into a KPI that ships to operators is the bug
 * we just paid down.
 */

import type cytoscape from 'cytoscape';

export type DemoCat = 'app' | 'pipeline' | 'storage' | 'analytics' | 'cross-border' | 'export' | 'db';
export type DemoSev = 'critical' | 'high' | 'medium' | 'low';

export interface DemoNode {
  id: string;
  label: string;
  cat: DemoCat;
  sev?: DemoSev;
  pii?: string[];
  rows?: number;
}

export const DEMO_NODES: DemoNode[] = [
  { id: 'src-onb',    label: 'Onboarding API', cat: 'app' },
  { id: 'src-mob',    label: 'Mobile App',     cat: 'app' },
  { id: 'src-br',     label: 'Branch CRM',     cat: 'app' },
  { id: 'oltp-cust',  label: 'rds.customers',     cat: 'db', sev: 'critical', pii: ['aadhaar','pan','phone'], rows: 1_240_000 },
  { id: 'oltp-kyc',   label: 'rds.kyc',           cat: 'db', sev: 'critical', pii: ['aadhaar','minor_dob'],   rows: 832 },
  { id: 'oltp-acct',  label: 'rds.accounts',      cat: 'db', sev: 'medium',   pii: ['ifsc'],                  rows: 2_400_000 },
  { id: 'oltp-tx',    label: 'rds.transactions',  cat: 'db', sev: 'low',      pii: ['phone'],                 rows: 184_000_000 },
  { id: 'etl-1',      label: 'Airflow ETL', cat: 'pipeline' },
  { id: 'etl-2',      label: 'dbt Models',  cat: 'pipeline' },
  { id: 'lake-raw',   label: 's3://lake/raw/',     cat: 'storage' },
  { id: 'lake-cur',   label: 's3://lake/curated/', cat: 'storage' },
  { id: 'dwh-cust',   label: 'snowflake.dim_customer',     cat: 'db', sev: 'high', pii: ['aadhaar','pan'], rows: 1_240_000 },
  { id: 'dwh-fact',   label: 'snowflake.fact_transactions', cat: 'db',                                       rows: 184_000_000 },
  { id: 'dwh-kyc',    label: 'snowflake.kyc_summary',      cat: 'db', sev: 'high', pii: ['aadhaar'],        rows: 832 },
  { id: 'rep-pub',    label: 's3://reports-public/', cat: 'export', sev: 'critical', pii: ['pan'], rows: 4_502 },
  { id: 'rep-bi',     label: 'Tableau BI',           cat: 'analytics' },
  { id: 'xb-bq',      label: 'bigquery.eu-west.users', cat: 'cross-border', sev: 'high', pii: ['email'], rows: 234_000 },
];

export const DEMO_EDGES: Array<{ source: string; target: string; risk?: 'public' | 'cross-border' }> = [
  { source: 'src-onb',  target: 'oltp-cust' },
  { source: 'src-mob',  target: 'oltp-cust' },
  { source: 'src-br',   target: 'oltp-cust' },
  { source: 'src-onb',  target: 'oltp-kyc' },
  { source: 'oltp-cust',target: 'oltp-acct' },
  { source: 'oltp-acct',target: 'oltp-tx' },
  { source: 'oltp-cust',target: 'etl-1' },
  { source: 'oltp-kyc', target: 'etl-1' },
  { source: 'oltp-tx',  target: 'etl-1' },
  { source: 'etl-1',    target: 'lake-raw' },
  { source: 'lake-raw', target: 'etl-2' },
  { source: 'etl-2',    target: 'lake-cur' },
  { source: 'etl-2',    target: 'dwh-cust' },
  { source: 'etl-2',    target: 'dwh-fact' },
  { source: 'etl-2',    target: 'dwh-kyc' },
  { source: 'dwh-cust', target: 'rep-pub', risk: 'public' },
  { source: 'dwh-fact', target: 'rep-bi' },
  { source: 'dwh-cust', target: 'xb-bq', risk: 'cross-border' },
];

export interface DemoExposurePath {
  id: string;
  title: string;
  hops: string[];
  riskKind: 'public' | 'cross-border' | 'minor';
  pii: string[];
  rationale: string;
  highlight: string[];
}

export const DEMO_EXPOSURE_PATHS: DemoExposurePath[] = [
  {
    id: 'pub-pan',
    title: 'PAN reaches a public bucket',
    hops: ['Onboarding API', 'rds.customers', 'Airflow', 'lake/raw', 'dbt', 'snowflake.dim_customer', 's3://reports-public/'],
    riskKind: 'public',
    pii: ['pan'],
    rationale: '7-hop journey ending at a public S3 bucket. 4,502 PAN values exposed. Detection blocks reads of dim_customer.pan with purpose != kyc.',
    highlight: ['src-onb','oltp-cust','etl-1','lake-raw','etl-2','dwh-cust','rep-pub'],
  },
  {
    id: 'xb-email',
    title: 'Email crosses to EU-West',
    hops: ['Onboarding API', 'rds.customers', 'Airflow', 'dbt', 'snowflake.dim_customer', 'bigquery.eu-west'],
    riskKind: 'cross-border',
    pii: ['email'],
    rationale: 'Cross-border transfer to bigquery.eu-west. Requires SCC + transfer-impact-assessment under DPDPA Section 16.',
    highlight: ['src-onb','oltp-cust','etl-1','etl-2','dwh-cust','xb-bq'],
  },
  {
    id: 'minor-dob',
    title: 'Minor DOB on the KYC path',
    hops: ['Onboarding API', 'rds.kyc', 'Airflow', 'dbt', 'snowflake.kyc_summary'],
    riskKind: 'minor',
    pii: ['minor_dob','aadhaar'],
    rationale: '832 records of minor data flowing to the warehouse. DPDPA Section 9 - verifiable parental consent required before processing.',
    highlight: ['src-onb','oltp-kyc','etl-1','etl-2','dwh-kyc'],
  },
];

export const DEMO_CAT_SHAPES: Record<DemoCat, cytoscape.Css.NodeShape> = {
  app:           'round-rectangle',
  pipeline:      'diamond',
  storage:       'barrel',
  analytics:     'hexagon',
  'cross-border':'tag',
  export:        'octagon',
  db:            'ellipse',
};
